import { useCallback, useEffect, useState } from 'react';

import { Shell } from '../componentes/Shell.jsx';
import { obtenerEstadoDelServicio } from '../nucleo/api/nucleoApi.js';
import {
  formatearFecha, obtenerDesplazamientoLegible, obtenerFechaDeNegocioHoy, obtenerZonaDetectada,
  resolverPeriodoLocal,
} from '../nucleo/fecha/fechaLocal.js';
import {
  anunciarVersionNueva, aplicarVersionPendiente, estadoDeActualizacion, suscribirAActualizacion,
} from '../nucleo/pwa/actualizacionDelServiceWorker.js';
import { useSesion } from '../nucleo/sesion/ProveedorDeSesion.jsx';
import { useTema } from '../nucleo/tema/ProveedorDeTema.jsx';
import { etiquetaDeMesYAnio } from '../modulos/tablero/logica/agregacion.js';

/**
 * VERSIÓN Y ACTUALIZACIÓN · F-02 (RF-024, RF-217, RN-616, D-317).
 *
 * LOS TRECE RENGLONES APROBADOS, COMPLETOS. Cuatro ya existían; nueve se
 * completan aquí, con el dato REAL de este dispositivo y de esta sesión.
 *
 * CUANDO UN DATO NO SE PUEDE LEER —navegador sin service worker, manifiesto
 * inaccesible, servidor que no respondió— EL RENGLÓN DICE «no disponible»
 * (RN-616). No se inventa el dato y NO se omite el renglón: así se sabe que se
 * intentó leer y no se pudo, en vez de dejar un hueco que parece normal.
 *
 * «Origen del tema» va FUNDIDO en el subtítulo del renglón del tema, tal como
 * está construido hoy; se conserva así por decisión (son dos de los trece).
 *
 * EL AVISO DE VERSIÓN NUEVA LLEVA SU BOTÓN «Actualizar ahora» (RF-217): buscar ya
 * NO aplica la actualización por su cuenta. Es lo que exige la deferencia del
 * service worker del lote UX (D-79 a D-90): una versión nueva nunca se activa por
 * debajo de una captura en curso ni sin que la persona lo diga.
 *
 * El renglón «Versión de catálogos» SE CONSERVA (D-317) y su dato lo publica el
 * servidor como tres conteos al vuelo, sin columna, sin tabla y sin caché.
 */

const NO_DISPONIBLE = 'no disponible';

/** Un renglón de dato: rótulo a la izquierda, valor a la derecha. */
function Dato({ rotulo, detalle, valor, resaltado = false, dato }) {
  return (
    <div className="fila">
      <div className="crece">
        <b>{rotulo}</b>
        {detalle && <small>{detalle}</small>}
      </div>
      <span
        className="ayuda"
        style={resaltado ? { color: 'var(--ok)', fontWeight: 700 } : undefined}
        data-prueba={dato}
      >
        {valor}
      </span>
    </div>
  );
}

export default function Acerca() {
  const { modo, temaEfectivo, origen } = useTema();
  const { sesion, haySesion } = useSesion();

  const [servicio, setServicio] = useState(null);
  const [servicioRespondio, setServicioRespondio] = useState(false);
  const [armazon, setArmazon] = useState(null);
  const [colorDelManifiesto, setColorDelManifiesto] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [actualizacion, setActualizacion] = useState(() => estadoDeActualizacion());

  const zona = obtenerZonaDetectada();
  const hoy = obtenerFechaDeNegocioHoy();

  /* Renglón 1 y 13 · lo que publica el servidor. */
  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        const respuesta = await obtenerEstadoDelServicio();
        if (!vigente) return;
        setServicio(respuesta ?? null);
        setServicioRespondio(Boolean(respuesta));
      } catch {
        if (vigente) { setServicio(null); setServicioRespondio(false); }
      }
    })();
    return () => { vigente = false; };
  }, []);

  /* Renglón 2 · el armazón que este dispositivo tiene guardado. Se lee del
     almacén de cachés del navegador; si no hay service worker o no hay caché, el
     renglón dice «no disponible» (RN-616). */
  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        if (typeof caches === 'undefined') return;
        const claves = await caches.keys();
        const delArmazon = (claves ?? []).find((clave) => String(clave).startsWith('gp-armazon'));
        if (vigente && delArmazon) setArmazon(delArmazon);
      } catch {
        if (vigente) setArmazon(null);
      }
    })();
    return () => { vigente = false; };
  }, []);

  /* Renglón 9 · color declarado del manifiesto de la aplicación instalable. */
  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        const enlace = document.querySelector('link[rel="manifest"]');
        if (!enlace?.href || typeof fetch !== 'function') return;
        const respuesta = await fetch(enlace.href);
        if (!respuesta?.ok) return;
        const manifiesto = await respuesta.json();
        if (vigente && manifiesto?.theme_color) setColorDelManifiesto(manifiesto.theme_color);
      } catch {
        if (vigente) setColorDelManifiesto(null);
      }
    })();
    return () => { vigente = false; };
  }, []);

  /* El aviso de versión nueva lo gobierna el gestor del núcleo, no esta pantalla. */
  useEffect(() => suscribirAActualizacion(setActualizacion), []);

  /**
   * BUSCAR NO ACTUALIZA (RF-217). Pregunta al service worker si hay versión nueva
   * y, si la hay, la deja EN ESPERA y anuncia el aviso con su botón. La
   * aplicación no se recarga aquí.
   */
  const buscarActualizacion = useCallback(async () => {
    setAviso(null);
    setBuscando(true);
    try {
      if (!('serviceWorker' in navigator)) {
        setAviso('Este navegador no puede instalar la aplicación, así que no hay nada que actualizar.');
        return;
      }

      const registro = await navigator.serviceWorker.getRegistration();
      if (!registro) {
        setAviso('La aplicación todavía no está instalada en este dispositivo.');
        return;
      }

      await registro.update();
      if (registro.waiting) {
        // Queda esperando; el aviso de abajo ofrece aplicarla.
        anunciarVersionNueva(registro.waiting);
      } else {
        setAviso('Ya tienes la versión más reciente.');
      }
    } catch {
      setAviso('No se pudo comprobar si hay una versión nueva. Revisa tu conexión y vuelve a intentarlo.');
    } finally {
      setBuscando(false);
    }
  }, []);

  /** El único momento en que la aplicación se recarga, y lo decide la persona. */
  const actualizarAhora = useCallback(() => {
    aplicarVersionPendiente({ forzar: true });
  }, []);

  /* --- Renglones ------------------------------------------------------- */
  const instaladaComoAplicacion = (() => {
    try {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
      if (window.navigator?.standalone === true) return true;
      return window.matchMedia('(display-mode: standalone)').matches;
    } catch {
      return null;
    }
  })();

  const conexionSegura = (() => {
    try {
      return window.location.protocol === 'https:';
    } catch {
      return null;
    }
  })();

  const idPeriodo = sesion?.idPeriodoActual ?? resolverPeriodoLocal(hoy);
  const catalogos = servicio?.versionDeCatalogos ?? null;

  return (
    <Shell titulo="Versión y actualización" volverA="/ajustes">
      {aviso && <div className="aviso i" role="status">{aviso}</div>}

      <div className="card">
        <h3>Gastos Personales</h3>

        {/* 1 */}
        <Dato rotulo="Versión instalada" valor={servicio?.version ?? NO_DISPONIBLE} dato="version-instalada" />
        {/* 2 */}
        <Dato
          rotulo="Armazón guardado en este dispositivo"
          detalle="Lo que permite que la aplicación abra sin espera"
          valor={armazon ?? NO_DISPONIBLE}
          dato="armazon-en-cache"
        />
        {/* 3 */}
        <Dato
          rotulo="Instalada en este dispositivo"
          valor={instaladaComoAplicacion === null
            ? NO_DISPONIBLE
            : (instaladaComoAplicacion ? 'Sí · se abre desde su propio icono' : 'No · se abre desde el navegador')}
          dato="instalada-como-aplicacion"
        />
        {/* 4 */}
        <Dato
          rotulo="Servidor"
          valor={`${servicioRespondio ? 'En línea' : 'Sin respuesta'} · ${
            conexionSegura === null ? NO_DISPONIBLE : (conexionSegura ? 'conexión segura' : 'conexión sin cifrar')
          }`}
          resaltado={servicioRespondio}
          dato="estado-del-servidor"
        />

        <button
          className="btn ancho"
          type="button"
          onClick={buscarActualizacion}
          disabled={buscando}
          data-prueba="buscar-actualizacion"
        >
          {buscando ? 'Buscando…' : 'Buscar actualización'}
        </button>

        {/* RF-217 · el aviso aprobado, con su botón. Buscar no recarga: esto sí. */}
        {actualizacion.hayVersionEnEspera && (
          <div className="aviso w" role="status" data-prueba="aviso-de-version-nueva">
            <b>Hay una versión nueva publicada</b>
            <div className="ayuda">
              Se instala cuando tú lo digas: hasta entonces sigues usando esta.
              {actualizacion.hayCapturaEnCurso && ' Lo que estás capturando se guarda en este dispositivo y no se pierde.'}
            </div>
            <button className="btn ancho" type="button" onClick={actualizarAhora} data-prueba="actualizar-ahora">
              Actualizar ahora
            </button>
          </div>
        )}
      </div>

      <div className="card">
        <h3>Diagnóstico</h3>

        {/* 5 */}
        <Dato rotulo="Fecha local del dispositivo" valor={formatearFecha(hoy)} dato="fecha-local" />
        {/* 6 */}
        <Dato
          rotulo="Zona detectada"
          detalle="No se configura: se detecta del dispositivo"
          valor={zona ? `${zona} (${obtenerDesplazamientoLegible()})` : NO_DISPONIBLE}
          dato="zona-detectada"
        />
        {/* 7 y 8 · el origen va fundido en el subtítulo, como estaba construido. */}
        <Dato
          rotulo="Tema en uso"
          detalle={origen === 'SISTEMA' ? 'Heredado del sistema operativo' : 'Elegido en este dispositivo'}
          valor={`${modo} → ${temaEfectivo}`}
          dato="tema-en-uso"
        />
        {/* 9 */}
        <Dato
          rotulo="Color declarado de la aplicación"
          valor={colorDelManifiesto ?? NO_DISPONIBLE}
          dato="color-del-manifiesto"
        />
        {/* 10 */}
        <Dato
          rotulo="Periodo actual"
          valor={`${idPeriodo} · ${etiquetaDeMesYAnio(hoy)}`}
          dato="periodo-actual"
        />
        {/* 11 */}
        <Dato
          rotulo="Persona en sesión"
          valor={sesion?.nombre ?? NO_DISPONIBLE}
          dato="persona-en-sesion"
        />
        {/* 12 · sesión viva ⇒ Activo: si desactivan el acceso, la sesión muere al
            instante y esta pantalla ya no se ve (D-09). */}
        <Dato
          rotulo="Estado del acceso"
          valor={haySesion ? 'Activo' : NO_DISPONIBLE}
          resaltado={Boolean(haySesion)}
          dato="estado-del-acceso"
        />
        {/* 13 · D-317 · los tres conteos que publica el servidor. */}
        <Dato
          rotulo="Versión de catálogos"
          valor={catalogos
            ? `cat-${catalogos.categorias} · mon-${catalogos.monedas} · usr-${catalogos.usuarios}`
            : NO_DISPONIBLE}
          dato="version-de-catalogos"
        />
      </div>

      <div className="info">
        La zona horaria y el tema aparecen aquí <b>solo como información de este dispositivo</b>. El
        tema se cambia en Ajustes › Apariencia; la zona se detecta y no se puede cambiar en ninguna
        pantalla. Ninguna de las dos viaja al servidor ni se guarda con tus datos.
      </div>
    </Shell>
  );
}
